import type { ComponentType, ReactElement, SVGProps } from 'react';
import {
  Monitor,
  LayoutGrid,
  ArrowRightLeft,
  Shield,
  Route,
  BarChart3,
  Users,
  AlertTriangle,
  Bell,
  Vault,
  Fuel,
  Database,
  Settings,
  Zap,
  ExternalLink,
  Copy,
} from 'lucide-react';

/**
 * String → component map. Used to render icons referenced by name in
 * env-driven service config (VITE_SERVICES_CONFIG). Names are PascalCase
 * without the trailing "Icon" suffix to keep config terse.
 *
 * Adding a new icon? Pick a lucide name from https://lucide.dev/icons/ and
 * add the entry here. Subsystems then reference it from env config.
 */
const REGISTRY: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  Monitor,
  Grid: LayoutGrid,
  ArrowsLeftRight: ArrowRightLeft,
  Shield,
  Route,
  Chart: BarChart3,
  Users,
  Alert: AlertTriangle,
  Bell,
  Vault,
  Gas: Fuel,
  Database,
  Settings,
  Zap,
  ExternalLink,
  Copy,
};

export const ICON_NAMES = Object.keys(REGISTRY);

export interface IconByNameProps extends SVGProps<SVGSVGElement> {
  /** Icon name from the registry (e.g. "Monitor", "Shield"). Case-sensitive. */
  name?: string;
  /** Fallback rendered when name is missing or unknown. */
  fallback?: ReactElement | null;
}

export function IconByName({ name, fallback = null, ...rest }: IconByNameProps) {
  if (!name) return fallback;
  const Cmp = REGISTRY[name];
  if (!Cmp) {
    if (typeof console !== 'undefined') {
      console.warn(`[icons] unknown icon name "${name}". Known: ${ICON_NAMES.join(', ')}`);
    }
    return fallback;
  }
  return <Cmp {...rest} />;
}
